import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { CopyService } from './abstract';

/**
 * This class is based on the code in the following projects:
 * https://github.com/zenorocha/select
 * https://github.com/zenorocha/clipboard.js/
 *
 * Both released under MIT license - © Zeno Rocha
 */
@Injectable()
export class CopyServiceImpl implements CopyService {
	constructor(@Inject(DOCUMENT) readonly doc: Document) {}

	private _textarea: HTMLTextAreaElement;

	copyText(text: string): Observable<boolean> {
		this._createTextareaAndSelect(text);

		const copySuccessful = this.doc.execCommand('copy');
		this._removeFake();

		return of(copySuccessful);
	}

	/**
	 * Creates a hidden textarea element, sets its value from `text` property,
	 * and makes a selection on it.
	 *
	 * @param {string} text
	 */
	private _createTextareaAndSelect(text: string): void {
		// Create a fake element to hold the contents to copy
		this._textarea = this.doc.createElement('textarea');

		// Prevent zooming on iOS
		this._textarea.style.fontSize = '12pt';

		// Hide the element
		this._textarea.classList.add('cdk-visually-hidden');

		// Move element to the same position vertically
		const yPosition = window.pageYOffset || this.doc.documentElement.scrollTop;
		this._textarea.style.top = yPosition + 'px';

		this._textarea.setAttribute('readonly', '');
		this._textarea.value = text;

		this.doc.body.appendChild(this._textarea);

		this._textarea.select();
		this._textarea.setSelectionRange(0, this._textarea.value.length);
	}

	private _removeFake(): void {
		if (this._textarea) {
			this.doc.body.removeChild(this._textarea);
			this._textarea = null;
		}
	}
}
